import { useRecordOnlyCount } from "@/lib/hooks/slides/useRecordOnlyCount";
import { useSetLessonSlidesRecordOnly } from "@/lib/hooks/slides/useSetLessonSlidesRecordOnly";
import { useSetProjectSlidesRecordOnly } from "@/lib/hooks/slides/useSetProjectSlidesRecordOnly";
import { Button, Modal } from "@ai-teach/ui-web";
import { useState } from "react";
import { MdKeyboardVoice, MdMicOff } from "react-icons/md";

const RING_RADIUS = 34;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function CountRing({ recordOnly, supported }: { recordOnly: number; supported: number }) {
  const ratio = supported === 0 ? 0 : recordOnly / supported;

  return (
    <div className="relative w-[88px] h-[88px] shrink-0">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 80 80" aria-hidden="true">
        <circle
          cx="40"
          cy="40"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="8"
          className="stroke-gray-200 dark:stroke-gray-700"
        />
        <circle
          cx="40"
          cy="40"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={RING_CIRCUMFERENCE * (1 - ratio)}
          className="stroke-primary-500 transition-[stroke-dashoffset] duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-semibold leading-none text-gray-900 dark:text-gray-100">
          {recordOnly}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">of {supported}</span>
      </div>
    </div>
  );
}

interface BulkRecordOnlyModalProps {
  type: "project" | "lesson";
  id: string;
  onClose: () => void;
}

export function BulkRecordOnlyModal({ type, id, onClose }: BulkRecordOnlyModalProps) {
  const { setProjectSlidesRecordOnly, loading: projectLoading } = useSetProjectSlidesRecordOnly();
  const { setLessonSlidesRecordOnly, loading: lessonLoading } = useSetLessonSlidesRecordOnly();
  const { count, loading: countLoading } = useRecordOnlyCount(type, id, true);
  const [pending, setPending] = useState<"on" | "off" | null>(null);

  const loading = projectLoading || lessonLoading;
  const supported = count?.supported ?? 0;
  const nothingToChange = !countLoading && supported === 0;

  const apply = async (recordOnly: boolean) => {
    setPending(recordOnly ? "on" : "off");
    try {
      if (type === "project") {
        await setProjectSlidesRecordOnly(id, recordOnly);
      } else {
        await setLessonSlidesRecordOnly(id, recordOnly);
      }
    } finally {
      setPending(null);
      onClose();
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Set record-only" size="sm">
      <div className="flex items-center gap-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-white/5 px-5 py-4">
        {countLoading || !count ? (
          <>
            <div className="w-[88px] h-[88px] rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-40 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
              <div className="h-3 w-28 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
            </div>
          </>
        ) : (
          <>
            <CountRing recordOnly={count.recordOnly} supported={supported} />
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {supported === 0
                  ? "No slides to change"
                  : `${count.recordOnly} of ${supported} are record-only`}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Word and reading slides only
              </p>
            </div>
          </>
        )}
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
        Overwrites every one of them in this {type}
        {type === "project" && ", its sub-projects and lessons"}.
      </p>

      <div className="flex justify-end gap-2 mt-6">
        <Button
          variant="ghost"
          leftIcon={<MdMicOff className="w-4 h-4" />}
          onClick={() => apply(false)}
          disabled={loading || countLoading || nothingToChange}
          isLoading={pending === "off"}
        >
          Turn off
        </Button>
        <Button
          variant="primary"
          leftIcon={<MdKeyboardVoice className="w-4 h-4" />}
          onClick={() => apply(true)}
          disabled={loading || countLoading || nothingToChange}
          isLoading={pending === "on"}
        >
          Turn on
        </Button>
      </div>
    </Modal>
  );
}
